import { api } from '@modules/common-utils-services/axiosUtil';
import { IconDefinition } from '@fortawesome/fontawesome-svg-core';
import { faCheckCircle, faExclamationCircle } from '@fortawesome/free-solid-svg-icons';

import API from '../../../services/API';
import { useAppSelector } from '../../../hooks';
import { configs } from '../../../constants/configs/configs';
import { setError, startSpinner, stopSpinner } from '../../../actions/common/actionCreators';
import { overrideDocument, setQLEVerificationsData } from '../../../actions/qle/qleActionCreators';
import { IQLEApplicants, IQLEEvents, IQLEVerificationDocumentResponse, VALIDATION_STATUS } from '../types/qleTypes';

const actions = { setError, startSpinner, stopSpinner };

const useQLEMN = () => {
    const { verificationsData }: { verificationsData: IQLEVerificationDocumentResponse } = useAppSelector(
        state => state.qle,
    );

    const fetchQLEVerificationsData = url => async dispatch => {
        const data: IQLEVerificationDocumentResponse = await API.getData(url, dispatch, actions, true);
        if (data) {
            dispatch(setQLEVerificationsData({ ...data }));
        } else {
            console.error('Cannot get QLE verifications data', url);
        }
    };

    const filterApplicants = (applicant: IQLEApplicants) =>
        applicant.events.length !==
        applicant.events.filter(event => event.validationStatus === VALIDATION_STATUS.NOT_REQUIRED).length;

    const areAllEventsVerified = (applicantEvents: IQLEEvents[]) => {
        let areAllEventsVerified = true;
        applicantEvents.forEach(event => {
            if (
                [
                    VALIDATION_STATUS.INITIAL,
                    VALIDATION_STATUS.PENDING,
                    VALIDATION_STATUS.SUBMITTED,
                    VALIDATION_STATUS.REJECTED,
                ].includes(event.validationStatus)
            ) {
                areAllEventsVerified = false;
            }
        });
        return areAllEventsVerified;
    };

    const getDocumentVerirficationStatus = (validationStatus: VALIDATION_STATUS): string | undefined => {
        if (
            [VALIDATION_STATUS.PENDING, VALIDATION_STATUS.SUBMITTED, VALIDATION_STATUS.REJECTED].includes(
                validationStatus,
            )
        )
            return 'qleMN_.notVerified';
        else if (
            [VALIDATION_STATUS.CANCELLED, VALIDATION_STATUS.VERIFIED, VALIDATION_STATUS.OVERRIDDEN].includes(
                validationStatus,
            )
        )
            return 'qleMN_.verified';
    };

    const getDocumentVerificationIcon = (validationStatus: VALIDATION_STATUS): IconDefinition => {
        if (
            [VALIDATION_STATUS.PENDING, VALIDATION_STATUS.SUBMITTED, VALIDATION_STATUS.REJECTED].includes(
                validationStatus,
            )
        )
            return faExclamationCircle;
        else if (
            [VALIDATION_STATUS.CANCELLED, VALIDATION_STATUS.VERIFIED, VALIDATION_STATUS.OVERRIDDEN].includes(
                validationStatus,
            )
        )
            return faCheckCircle;
        return faExclamationCircle;
    };

    const isDocumentVerified = (validationStatus: VALIDATION_STATUS): boolean => {
        return (
            validationStatus === VALIDATION_STATUS.VERIFIED ||
            validationStatus === VALIDATION_STATUS.CANCELLED ||
            validationStatus === VALIDATION_STATUS.OVERRIDDEN
        );
    };

    const isOverrideButtonVisible = (validationStatus: VALIDATION_STATUS): boolean => {
        return (
            validationStatus !== VALIDATION_STATUS.VERIFIED &&
            validationStatus !== VALIDATION_STATUS.CANCELLED &&
            validationStatus !== VALIDATION_STATUS.OVERRIDDEN
        );
    };

    const submitOverrideComment =
        ({ applicantEventId, comment, caseNumber }) =>
            async dispatch => {
                const url = configs.endpoints.QLE_DOCUMENTS_OVERRRIDE;

                var formData = new FormData();
                formData.append('applicantEventId', applicantEventId);
                formData.append('overrideComments', comment);
                formData.append('caseNumber', caseNumber);

                const response = await api.post(url, formData);
                if (response.data === 200) {
                    const { applicants } = verificationsData;
                    dispatch(overrideDocument({
                        ...verificationsData,
                        applicants: [
                            ...applicants.map(applicant => {
                                return {
                                    ...applicant,
                                    events: [
                                        ...applicant.events.map(event => {
                                            if (event.applicantEventId === applicantEventId) {
                                                return {
                                                    ...event,
                                                    validationStatus: VALIDATION_STATUS.OVERRIDDEN,
                                                };
                                            }
                                            return event;
                                        }),
                                    ],
                                };
                            }),
                        ],
                    }));
                };
            };

    return {
        fetchQLEVerificationsData,
        getDocumentVerirficationStatus,
        areAllEventsVerified,
        filterApplicants,
        getDocumentVerificationIcon,
        isOverrideButtonVisible,
        submitOverrideComment,
        isDocumentVerified,
    };
};

export default useQLEMN;
